import _ from "underscore";
import { removeMd5FromStart } from "@exo/helpers";
import { describeFilters, orFilterGroup } from "lib/helpers/helpers.js";

// filterByTopic
// findLargestBarValue
// forceArrayToActLikeAnObject
// formatDisplayFilters
// formatCrosstabsStats
// formatNarrativeBarGraph
// enrichEntities
// enrichNarratives
// setStatTotals
// sortAndStabilizeStats
// transformBarChartLabel


// Returns all the narratives if there are any issues
export const filterByTopic = (params) => {
	// console.log("filterByTopic", params);
	const { narratives, currentTopic } = params;

	if (!currentTopic || currentTopic.id === "narratives") {
		const allTopics = _.filter(narratives, (narrative) => {
			return narrative.parent === "narratives";
		});
		return allTopics || narratives; // return all if undefined
	} else {
		const narrativesForCurrentTopic = _.filter(narratives, (narrative) => {
			return narrative.parent === currentTopic.id;
		});
		return narrativesForCurrentTopic || narratives; // return all if undefined
	}
};

// returns number or false (if any values are set to disableMax) // FLAG_DISABLE_MAX
export const findLargestBarValue = (params) => {
	const { bars } = params;

	const barsToEvaluate = _.filter(bars, (bar) => {
		return bar;
	});

	if (!barsToEvaluate || barsToEvaluate.length < 2) {
		return false;
	} else {
		const topValues = [];
		_.each(barsToEvaluate, (barSet) => {
			// FLAG_DISABLE_MAX - not installed, may need to rethink implementation
			if (barSet.disableMax) {
				return false;
			} else {
				topValues.push({ value: reduceTopValue({ bars: barSet }) });
			}
		});

		const topOfGroup = reduceTopValue({ bars: topValues });

		return topOfGroup;
	}
};

const reduceTopValue = (params) => {
	const { bars } = params;

	return _.reduce(
		bars,
		(memo, item) => {
			return memo > item.value ? memo : item.value;
		},
		bars[0] ? bars[0].value : 0 // pass the first score instead of the first object
	);
};

// FLAG_ENABLE_MULTIPLE_NARRATIVE_FILTERS - remove when no longer necessary
// Array should always be length 0 or 1, but this isn't enforced here
// Use case here is to use an array to hold the results of a radio button. Using an array because we'll update to a checkbox format in the future
export const forceArrayToActLikeAnObject = (params) => {
	const { array, object, key } = params;

	const objectIsInArray = !!_.find(array, (arrayObj) => {
		return object[key] === arrayObj[key];
	});

	const turnItOff = [];
	const turnItOn = [object];

	if (objectIsInArray) {
		return turnItOff;
	} else {
		return turnItOn;
	}
};

export const formatDisplayFilters = (params) => {
	const { currentTopic, entityFilters, narrativeFilters } = params;
	const narrativeFiltersClone = narrativeFilters && narrativeFilters.length > 0 ? JSON.parse(JSON.stringify(narrativeFilters)) : [];

	const narrativeFiltersWithoutTopicsUnlessInAll = _.reject(narrativeFiltersClone, (filter) => {
		const notInAllTopicsMode = currentTopic.id !== "narratives";
		const filterIsATopic = filter.parent === "narratives";
		const filterIsAllTopics = filter.id === "narratives";

		return filterIsAllTopics || (notInAllTopicsMode && filterIsATopic);
	});

	return [...entityFilters, ...narrativeFiltersWithoutTopicsUnlessInAll];
};

export const formatCrosstabsStats = (params) => {
	const { config, stats } = params;

	if (!config) {
		return false;
	} else if (!stats[config.id]) {
		return [];
	} else {
		const { id, desiredOrder, limit } = config;

		const formattedEntities = enrichEntities({
			entities: stats[id],
			type: id,
			limit,
		});
		const stabilizedStats = sortAndStabilizeStats({ stats: formattedEntities, desiredOrder });

		return stabilizedStats;
	}
};

export const formatNarrativeBarGraph = (params) => {
	const { crosstabsNarrativeHeaders, labels, currentNarrativeFacet, desiredOrder, narrativeData } = params;

	if (
		!crosstabsNarrativeHeaders ||
		!currentNarrativeFacet ||
		!narrativeData ||
		_.isEmpty(crosstabsNarrativeHeaders) ||
		_.isEmpty(currentNarrativeFacet) ||
		_.isEmpty(narrativeData)
	) {
		console.warn("not enough data");
		return [];
	} else {
		const currentHeaders = _.reject(crosstabsNarrativeHeaders[currentNarrativeFacet.id], (facet) => {
			// Get rid of Narrative and Totals
			return facet.id === "totalCount" || facet.id === "name";
		});

		// Split into groups for multiple charts
		const groups = _.groupBy(currentHeaders, "groupBy");
		const groupKeys = Object.keys(groups);
		const multipleCharts = groupKeys && groupKeys.length > 1;

		if (multipleCharts) {
			const graphGroup = [];
			_.each(groupKeys, (key) => {
				const facetGroup = groups[key];
				const groupData = [];
				const groupMeta =
					labels &&
					_.find(labels, (meta) => {
						return meta.groupBy === key;
					});

				_.each(facetGroup, (facet) => {
					const value = narrativeData[facet.id];
					groupData.push({ label: facet.name, value: value || 0 });
				});

				graphGroup.push({ title: (groupMeta && groupMeta.label) || key, data: groupData });
			});

			return graphGroup;
		} else if (desiredOrder) {
			const desiredData = [];
			const desiredMeta =
				labels &&
				groupKeys &&
				groupKeys.length === 1 &&
				_.find(labels, (meta) => {
					return meta.groupBy === groupKeys[0];
				});

			_.each(desiredOrder, (desiredId) => {
				const facet = _.find(currentHeaders, (data) => {
					return data.id === desiredId;
				});
				const value = facet ? narrativeData[facet.id] : 0;
				if (_.isNumber(value)) {
					desiredData.push({ label: facet.name, value });
				}
			});

			return [{ title: (desiredMeta && desiredMeta.label) || "Volume by category", data: desiredData }];
		} else {
			const graphData = [];

			_.each(currentHeaders, (facet) => {
				const value = narrativeData[facet.id];
				if (value && _.isNumber(value)) {
					graphData.push({ label: facet.name, value });
				}
			});

			return [{ title: "Volume by category", data: graphData }];
		}
	}
};

export const enrichEntities = (params) => {
	const { entities, type, limit } = params;

	if (!entities) {
		return [];
	} else {
		const entitiesArray = [];
		const entityNames = Object.keys(entities);

		_.each(entityNames, (name) => {
			const entity = {
				name,
				label: name, // for BarChart
				id: name,
				value: entities[name],
				parent: "entities",
				type,
			};
			entitiesArray.push(entity);
		});

		return limit ? _.first(entitiesArray, limit) : entitiesArray;
	}
};

// NOTE: assumes topics all have the parent "narratives"
export const enrichNarratives = (params) => {
	const { narrativeScores, flatNarratives } = params;
	if (!narrativeScores || _.isEmpty(narrativeScores)) {
		return [];
	} else {
		const narrativeIds = Object.keys(narrativeScores);

		const narrativesWithData = _addNarrativeData({ narrativeIds, narrativeScores, flatNarratives });
		// const narrativesWithoutTopics = _removeTopics({ narratives: narrativesWithData });
		// const narrativesWithLegibleParentNames = _addLegibleParentNames({
		// 	narratives: narrativesWithoutTopics,
		// 	flatNarratives,
		// });

		return narrativesWithData;
	}
};

export const sumColumns = (params) => {
	const { narrativesArray, desiredOrder } = params;
	const narrativeSums = {};

	_.each(desiredOrder, (key) => {
		const values = _.pluck(narrativesArray, key);
		const sum = values.reduce(_sumReducer, 0);
		narrativeSums[key] = sum;
	});

	return narrativeSums;
};

export const setStatTotals = (params) => {
	const { totalArticles, stats, desiredOrder, currentTopic } = params;
	// console.log("setStatTotals", params);

	const topStats = {
		totalCount: totalArticles,
		name: currentTopic.id === "narratives" ? "Total Articles Collected" : `Total Articles Matching ${currentTopic.name}`,
		floatingRow: true,
	};

	_.each(desiredOrder, (name) => {
		const columnStats = _.find(stats, (data) => {
			return data && data.name && removeMd5FromStart({ string: data.name }) === name;
		});
		topStats[name] = columnStats ? columnStats.value : 0;
	});

	return [topStats];
};

// NOTE: stats may or may not have the md5_ prefix
export const sortAndStabilizeStats = (params) => {
	const { desiredOrder, stats } = params;

	if (!desiredOrder) {
		return stats;
	} else {
		const remainingStats = _.pluck(stats, "name");
		const sortedStats = [];

		// loop through desiredOrder and copy data in a new array.
		// Delete keys as you go
		_.each(desiredOrder, (desiredName) => {
			const data = _.find(stats, (obj) => {
				return removeMd5FromStart({ string: obj.id }) === desiredName;
			});

			const indexToRemove = _.findIndex(remainingStats, (bias) => {
				return removeMd5FromStart({ string: bias }) === desiredName;
			});
			if (indexToRemove >= 0) {
				remainingStats.splice(indexToRemove, 1);
			}

			sortedStats.push({
				name: desiredName,
				label: data ? data.label : desiredName,
				id: data ? data.id : desiredName,
				value: data ? data.value : 0,
				parent: data ? data.parent : "entities",
				type: data ? data.type : "Entities",
			});
		});

		// Add any stat that wasn't on our list
		_.each(remainingStats, (stat) => {
			const data = _.find(desiredOrder, (obj) => {
				return obj.id === stat;
			});
			if (data) {
				sortedStats.push(data);
			}
		});

		return sortedStats;
	}
};

export const transformBarChartLabel = (params) => {
	const { row, context } = params;
	const { label, name } = row;
	return removeMd5FromStart({ string: label || name });
};

export const writeTitle = (params) => {
	const { entity, displayFilters, currentTopic, selectedDateRange, mapFilters, singleNarrative } = params;

	const filtersWithoutMaps = _.reject(displayFilters, (filter) => {
		return filter.parent === "sourceState";
	});
	const basicFilterText = describeFilters({ filtersList: filtersWithoutMaps });
	const basicFilterString = basicFilterText ? ` AND ${basicFilterText}` : "";
	const mapFilterText = orFilterGroup({ filtersList: mapFilters });
	const mapFilterString = mapFilterText ? ` AND ${mapFilterText}` : "";
	const narrativeDescription = singleNarrative ? "" : " Narratives";

	return `Distribution of Articles Containing "${entity.name}"${basicFilterString}${mapFilterString} Across ${
		currentTopic.id === "narratives" ? "Topics" : `${currentTopic.name}${narrativeDescription}`
	} ${selectedDateRange.name && selectedDateRange.name !== "History" ? selectedDateRange.name : "Historically"}`;
};

// -------------------------------- Local functions ------------------------------------------------- //
const _sumReducer = (memo, number) => {
	return memo + number;
};

const _addNarrativeData = (params) => {
	const { narrativeIds, narrativeScores, flatNarratives } = params;
	const narrativesArray = [];

	_.each(narrativeIds, (narId) => {
		const narrative = _.find(flatNarratives, (nar) => {
			return nar.id === narId;
		});

		if (narrative) {
			let narrativeCopy = JSON.parse(JSON.stringify(narrative));
			if (_.isObject(narrativeScores[narId])) {
				narrativeCopy = _explodeNarrativeObject({
					narrative: narrativeCopy,
					object: narrativeScores[narId],
				});
			} else {
				narrativeCopy.score = narrativeScores[narId];
			}
			narrativesArray.push(narrativeCopy);
		}
	});

	return narrativesArray;
};

const _removeTopics = (params) => {
	const { narratives } = params;

	const withoutTopics = _.reject(narratives, (nar) => {
		return !nar.parent || nar.parent === "narratives";
	});

	return withoutTopics;
};

const _addLegibleParentNames = (params) => {
	const { narratives, flatNarratives } = params;
	const narrativeCopy = JSON.parse(JSON.stringify(narratives));

	const parentsOnly = _.filter(flatNarratives, (nar) => {
		return nar.parent === "narratives";
	});

	_.each(narrativeCopy, (nar) => {
		const parent = _.find(parentsOnly, (topic) => {
			return topic.id === nar.parent;
		});

		nar.parentName = parent.name;
	});

	return narrativeCopy;
};

// mutates narrative
const _explodeNarrativeObject = (params) => {
	const { narrative, object } = params;

	if (!object || !_.isObject(object)) {
		return narrative;
	} else {
		const keysList = Object.keys(object);

		_.each(keysList, (objectKey) => {
			narrative[objectKey] = object[objectKey];
		});

		return narrative;
	}
};

// ------- Text helpers ---------
export const biasText = "The number of articles published by outlets with a political bias.";
export const peopleText = "The number of articles a person is mentioned in.";
